// src/pages/Checkout.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

/** =================== KONFIG =================== **
 * Versand-Regeln (dein gewünschtes Verhalten):
 *  1) Wenn eine Betonrohrfalle im Warenkorb ist -> immer Spedition.
 *     + ggf. PLZ-Zonenaufschlag (nur für Spedition / Betonrohrfalle).
 *  2) Sonst: Wenn Abfangkasten ODER Schubstange im Warenkorb ist -> DHL Sperrgut.
 *     (egal ob 1, 2 oder 3 Schubstangen: es bleibt 1 Sperrgut-Sendung)
 *  3) Sonst -> normales DHL Paket (Gewichtsstufen optional).
 *
 * Hinweis: Express nur bei normalem DHL Paket (optional), nicht bei Sperrgut/Spedition.
 */

// Slug-Gruppen für die Versandlogik
const SPEDITION_SLUGS = new Set(["betonrohrfalle"]);
const SPERRGUT_SLUGS = new Set(["schubstange", "abfangkasten"]);

// DHL Sperrgut Pauschale (EUR) – DE Beispiel. Für AT/CH ggf. anpassen.
const DHL_SPERRGUT_FLAT_EUR = { DE: 40.0, AT: 55.0, CH: 0 };

// Gewicht (Fallback in GRAMM), falls API kein weight_grams liefert
const WEIGHT_FALLBACK_BY_SLUG = {
    betonrohrfalle: 130000, // 130 kg (Beispiel)
    abfangkasten: 4500,
    schubstange: 1200,
    fallenmelder: 300,
    catchalive: 300,
    fallenpaesse: 100,
    fangplaetze: 200,
    koeder: 200,
};

// Produktspezifische Fracht-Fallbacks (in CENT), falls API kein freight_cents liefert
const FREIGHT_FALLBACK_BY_SLUG = {
    betonrohrfalle: 5900,  // 59,00 € (wird im Gewichtspfad NICHT genutzt)
    abfangkasten: 3000,
    schubstange: 690,
    fallenmelder: 390,
    catchalive: 390,
    fallenpaesse: 190,
    fangplaetze: 190,
    koeder: 190,
};

// DE-PLZ-Zonenaufschläge (in CENT)
const DE_ZONE_SURCHARGE = [
    // 🏝 Inseln (immer zuerst!)
    { from: "18565", to: "18565", zone_cents: 0, label: "Inseln" }, // Hiddensee

    // Nordfriesische Inseln
    { from: "25845", to: "25845", zone_cents: 8000, label: "Inseln" },
    { from: "25846", to: "25847", zone_cents: 8000, label: "Inseln" },
    { from: "25849", to: "25849", zone_cents: 8000, label: "Inseln" },
    { from: "25859", to: "25859", zone_cents: 8000, label: "Inseln" },
    { from: "25863", to: "25863", zone_cents: 8000, label: "Inseln" },
    { from: "25869", to: "25869", zone_cents: 8000, label: "Inseln" },
    { from: "25929", to: "25933", zone_cents: 8000, label: "Inseln" },
    { from: "25938", to: "25942", zone_cents: 8000, label: "Inseln" },
    { from: "25946", to: "25949", zone_cents: 8000, label: "Inseln" },
    { from: "25952", to: "25955", zone_cents: 8000, label: "Inseln" },
    { from: "25961", to: "25970", zone_cents: 8000, label: "Inseln" },
    { from: "25980", to: "25980", zone_cents: 8000, label: "Inseln" },
    { from: "25985", to: "25986", zone_cents: 8000, label: "Inseln" },
    { from: "25988", to: "25990", zone_cents: 8000, label: "Inseln" },
    { from: "25992", to: "25994", zone_cents: 8000, label: "Inseln" },
    { from: "25996", to: "25999", zone_cents: 8000, label: "Inseln" },

    // Ostfriesische Inseln
    { from: "26465", to: "26465", zone_cents: 8000, label: "Inseln" },
    { from: "26474", to: "26474", zone_cents: 8000, label: "Inseln" },
    { from: "26486", to: "26486", zone_cents: 8000, label: "Inseln" },
    { from: "26548", to: "26548", zone_cents: 8000, label: "Inseln" },
    { from: "26571", to: "26571", zone_cents: 8000, label: "Inseln" },
    { from: "26579", to: "26579", zone_cents: 8000, label: "Inseln" },
    { from: "26757", to: "26757", zone_cents: 8000, label: "Inseln" },

    // Helgoland
    { from: "27498", to: "27498", zone_cents: 8000, label: "Inseln" },

    // Bestehende Zonen
    { from: "01000", to: "19999", zone_cents: 900, label: "Nord/Ost" },
    { from: "20000", to: "29999", zone_cents: 1200, label: "Nord" },
    { from: "30000", to: "69999", zone_cents: 500, label: "West/Mitte" },
    { from: "70000", to: "99999", zone_cents: 0, label: "Süd" },
];


// Länderpauschale (falls kein DE/PLZ – hier nur AT aktiviert)
const COUNTRY_ZONE_FLAT = {
    DE: null,
    AT: 1500, // 15,00 €
    // CH: 2500,
};

// DHL Gewichtsstufen (kg) -> Grundpreis in EUR (reiner Gewichtsteil, ohne Zone)
const DHL_WEIGHT_BASE_EUR = [
    { maxKg: 5, base: 8 },
    { maxKg: 10, base: 12 },
    { maxKg: 31.5, base: 19 },
    { maxKg: 80.5, base: 39 },
];
// Express-Zuschlag (nur bei DHL)
const DHL_EXPRESS_SURCHARGE = { DE: 5, AT: 7, CH: 9 };

// Spedition (EUR): Grundpreise + Zuschlag pro angefangene 100 kg
const FREIGHT_BASE = { DE: 129.9, AT: 139.9, CH: 89.9 };
const FREIGHT_STEP_KG = 100;
const FREIGHT_STEP_SURCHARGE = 30.0; // je weitere 100 kg ab 200kg
const DHL_MAX_KG = 80.5; // darüber Spedition

/** ============== Helpers (PLZ, Zonen, Gewicht) ============== */
function sanitizeZipTyping(value = "") {
    return (value || "").replace(/\D/g, "").slice(0, 5);
}
function isValidDEZip(zip = "") {
    return /^\d{5}$/.test(zip);
}
function findDEZone(zip) {
    const s = String(zip).replace(/\D/g, "");
    if (s.length !== 5) return { zone_cents: 0, label: null };
    const n = parseInt(s, 10);
    for (const z of DE_ZONE_SURCHARGE) {
        const a = parseInt(z.from, 10);
        const b = parseInt(z.to, 10);
        if (n >= a && n <= b) return { zone_cents: z.zone_cents, label: z.label };
    }
    return { zone_cents: 0, label: "Zone (Default)" };
}
function dhlWeightBaseForKg(kg) {
    for (const t of DHL_WEIGHT_BASE_EUR) if (kg <= t.maxKg) return t.base;
    return Infinity; // > DHL_MAX_KG
}

/** =================== CHECKOUT =================== */
export default function Checkout({ cart, clearCart }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [street, setStreet] = useState("");
    const [city, setCity] = useState(""); 

    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const [order, setOrder] = useState(null);
    const [done, setDone] = useState(null);
    const ppRef = useRef(null);

    // Eingaben
    const [country, setCountry] = useState("DE");
    const [zip, setZip] = useState("");
    const [method, setMethod] = useState("standard"); // "standard" | "express" | "pickup"
    const [paymentMethod, setPaymentMethod] = useState("paypal"); // "paypal" | "invoice"

    // Anzeige/Berechnung
    const [zoneLabel, setZoneLabel] = useState(null);
    const [shippingEUR, setShippingEUR] = useState(0);
    const [carrier, setCarrier] = useState("DHL"); // "DHL" | "Spedition" | "Pickup"

    // Mobile summary toggle
    const [summaryOpen, setSummaryOpen] = useState(false);

    // Produkte laden
    useEffect(() => {
        setLoading(true);
        fetch(`${API_BASE}/api/products.php?v=ship`, { credentials: "omit" })
            .then((r) => r.json())
            .then(setAllProducts)
            .catch(() => setAllProducts([]))
            .finally(() => setLoading(false));
    }, []);

    // Warenkorb + Daten mergen (inkl. Varianten)
    const items = useMemo(() => {
        const map = new Map(allProducts.map((p) => [p.slug, p]));

        return cart
            .map((it) => {
                const p = map.get(it.slug);

                // Variantenwerte aus dem Cart bevorzugen (falls vorhanden)
                const cartVariantId = it.variant_id ?? it.variantId ?? null;
                const cartVariantLabel = it.variant_label ?? it.variantLabel ?? null;

                const cartPriceCents = it.price_cents ?? it.priceCents ?? it.variant_price_cents ?? it.variantPriceCents ?? null;
                const cartWeightGrams = it.weight_grams ?? it.weightGrams ?? it.variant_weight_grams ?? it.variantWeightGrams ?? null;

                const unitPriceCents = Number(cartPriceCents ?? p?.price_cents ?? 0);

                // Gewicht: Variante > Produkt > Fallback
                const unitWeightGrams = Number(
                    cartWeightGrams ??
                        p?.weight_grams ??
                        (WEIGHT_FALLBACK_BY_SLUG[it.slug] ?? 1000)
                );

                // freight_cents kommt weiterhin vom Produkt (Variantentabelle hat das i.d.R. nicht)
                const unitFreightCents = Number(
                    p?.freight_cents != null ? p.freight_cents : (FREIGHT_FALLBACK_BY_SLUG[it.slug] ?? 0)
                );

                const titleBase = p?.title || it.slug;
                const title = cartVariantLabel ? `${titleBase} – ${cartVariantLabel}` : titleBase;

                return {
                    slug: it.slug,
                    qty: it.qty,
                    title,
                    // zusätzlich fürs Backend
                    variant_id: cartVariantId,
                    variant_label: cartVariantLabel,
                    price_cents: unitPriceCents,
                    weight_grams: unitWeightGrams,
                    freight_cents: unitFreightCents,
                };
            })
            .filter((it) => it.price_cents >= 0 && it.qty > 0);
    }, [cart, allProducts]);

    const hasSpeditionItem = useMemo(
        () => items.some((it) => SPEDITION_SLUGS.has(it.slug) && it.qty > 0),
        [items]
    );

    const hasSperrgutItem = useMemo(
        () => items.some((it) => SPERRGUT_SLUGS.has(it.slug) && it.qty > 0),
        [items]
    );

    // Zwischensumme Warenwert
    const itemsTotalCents = items.reduce((s, it) => s + it.price_cents * it.qty, 0);
    const itemsTotalEUR = itemsTotalCents / 100;

    // Gewicht gesamt (alle Artikel)
    const totalWeightKg = useMemo(
        () => items.reduce((g, it) => g + (it.weight_grams * it.qty) / 1000, 0),
        [items]
    );

    // (Alt) Produktspezifische Fracht – wird in der neuen Regel-Logik nicht mehr genutzt,
    // bleibt aber als Fallback/Referenz erhalten.
    const productFreightCents = useMemo(
        () => items.reduce((s, it) => s + it.freight_cents * it.qty, 0),
        [items]
    );

    // Versandberechnung (Optimierte Logik)
    useEffect(() => {
        if (method === "pickup") {
            setShippingEUR(0);
            setCarrier("Pickup");
            setZoneLabel(null);
            return;
        }

        const kg = Math.max(0, totalWeightKg);
        
        // Spezialfall: Abfangkasten Menge prüfen (nur 1 geht per Sperrgut)
        const abfangkastenItem = items.find(it => it.slug === "abfangkasten");
        const abfangkastenQty = abfangkastenItem ? abfangkastenItem.qty : 0;

        // 1) SPEDITION-Zwang: Durch Produkt, Gesamtgewicht ODER > 1 Abfangkasten
        const needsSpedition = hasSpeditionItem || kg > DHL_MAX_KG || abfangkastenQty > 1;

        // --- 1) SPEDITION ---
        if (needsSpedition) {
            let zoneCents = 0;
            let zLabel = null;

            if (country === "DE" && isValidDEZip(zip)) {
                const z = findDEZone(zip);
                zoneCents = z.zone_cents || 0;
                zLabel = z.label;
            } else {
                const flat = COUNTRY_ZONE_FLAT[country] || 0;
                zoneCents = flat;
                zLabel = flat > 0 ? `Region: ${country}` : null;
            }

            // NEU: Light-Spedition Tarif bis 84kg (einschließlich)
            // Ab 85kg gilt die volle Grundfracht (129.90€ / 139.90€)
            let base = FREIGHT_BASE[country] ?? 129.9;
            if (kg <= 84.9) {
                base = 80.0;
            }

            // Zuschlag: Erst ab einem Speditionsgewicht von 200kg
            // Schritte von 100kg à 30€
            const over = Math.max(0, kg - 200);
            const steps = Math.ceil(over / FREIGHT_STEP_KG);
            const weightSurcharge = steps * FREIGHT_STEP_SURCHARGE;
            const shippingCents = Math.round(base * 100) + zoneCents + Math.round(weightSurcharge * 100);

            setCarrier("Spedition");
            setZoneLabel(zLabel);
            setShippingEUR(Math.max(0, shippingCents) / 100);
            return;
        }

        // --- 2) DHL SPERRGUT ---
        if (hasSperrgutItem) {
            const flat = DHL_SPERRGUT_FLAT_EUR[country] ?? DHL_SPERRGUT_FLAT_EUR.DE;
            setCarrier("DHL Sperrgut");
            setZoneLabel(null);
            setShippingEUR(Math.max(0, flat));
            return;
        }

        // --- 3) DHL PAKET ---
        let base = dhlWeightBaseForKg(kg);
        // Falls aus irgendeinem Grund kein Preis gefunden wurde (unwahrscheinlich wegen check oben)
        if (!isFinite(base)) base = 129.9; 

        if (method === "express") {
            base += DHL_EXPRESS_SURCHARGE[country] ?? 0;
        }

        setCarrier("DHL Paket");
        setZoneLabel(null);
        setShippingEUR(Math.max(0, base));
    }, [method, country, zip, hasSpeditionItem, hasSperrgutItem, totalWeightKg]);

    // Express automatisch deaktivieren, wenn Sperrgut oder Spedition nötig ist
    useEffect(() => {
        if (method === "express" && (hasSpeditionItem || hasSperrgutItem)) {
            setMethod("standard");
        }
    }, [method, hasSpeditionItem, hasSperrgutItem]);

    const grandTotalEUR = itemsTotalEUR + (shippingEUR || 0);
    // Für Backend/PayPal: immer in CENT rechnen, damit es keine Rundungsfehler gibt
    const shippingTotalCents = Math.round((shippingEUR || 0) * 100);
    const grandTotalCents = itemsTotalCents + shippingTotalCents;

    /** ================= API ================= */
    async function createOrderInDB() {
        // Effektive Methode (Express nur bei DHL Paket)
        const effectiveMethod =
            method === "express" && (hasSpeditionItem || hasSperrgutItem) ? "standard" : method;

        // Gleiche Bedingungen wie in der Versand-Anzeige (useEffect oben)!
        const kgTotal = Math.max(0, totalWeightKg);
        const abfangkastenQtyDB = items.find(it => it.slug === "abfangkasten")?.qty ?? 0;
        const needsSpedition = hasSpeditionItem || kgTotal > DHL_MAX_KG || abfangkastenQtyDB > 1;

        // Zoneninfo nur für Spedition
        const zoneInfo = (() => {
            if (!needsSpedition) return { zone_cents: 0, label: null };
            if (country === "DE" && isValidDEZip(zip)) return findDEZone(zip);
            const flat = COUNTRY_ZONE_FLAT[country];
            return { zone_cents: typeof flat === "number" ? flat : 0, label: flat != null ? `Land: ${country}` : null };
        })();

        let shippingPayload;

        if (effectiveMethod === "pickup") {
            shippingPayload = {
                method: effectiveMethod,
                country,
                zip,
                path: "pickup",
                carrier: "Pickup",
                cost_cents: 0,
                zone_cents: 0,
                breakdown: { note: "pickup" },
            };
        } else if (needsSpedition) {
            // Spedition (Betonrohrfalle, Übergewicht oder >1 Abfangkasten)
            const kg = kgTotal;
            
            // Logik: Bis 84.9kg Grundpreis 80€, ab 85kg reguläre Grundfracht (129.90€)
            let baseEUR = kg <= 84.9 ? 80.0 : (FREIGHT_BASE[country] ?? 129.9);
            
            // Zuschlag erst ab 200kg, in 100kg Schritten à 30€
            const over = Math.max(0, kg - 200);
            const steps = Math.ceil(over / FREIGHT_STEP_KG);
            const surcharge_cents = Math.round(steps * FREIGHT_STEP_SURCHARGE * 100);
            
            const freight_cents = Math.round(baseEUR * 100) + surcharge_cents;
            const zone_cents = zoneInfo.zone_cents || 0;
            
            shippingPayload = {
                method: effectiveMethod,
                country,
                zip,
                path: "spedition_zone",
                carrier: "Spedition",
                cost_cents: freight_cents + zone_cents,
                zone_cents,
                breakdown: {
                    total_weight_kg: kg,
                    base_freight_cents: Math.round(baseEUR * 100),
                    step_kg: FREIGHT_STEP_KG,
                    steps,
                    step_surcharge_cents: Math.round(FREIGHT_STEP_SURCHARGE * 100),
                    freight_cents,
                    zone_cents,
                    zone_label: zoneInfo.label || null,
                },
            };
        } else if (hasSperrgutItem) {
            // DHL Sperrgut pauschal
            const flatEUR = DHL_SPERRGUT_FLAT_EUR[country] ?? DHL_SPERRGUT_FLAT_EUR.DE;
            const cost_cents = Math.round(flatEUR * 100);
            shippingPayload = {
                method: effectiveMethod,
                country,
                zip,
                path: "dhl_sperrgut_flat",
                carrier: "DHL Sperrgut",
                cost_cents,
                zone_cents: 0,
                breakdown: {
                    flat_cents: cost_cents,
                    note: "sperrgut overrides parcel",
                },
            };
        } else {
            // DHL Paket nach Gewicht
            const kg = Math.max(0, totalWeightKg);
            let baseEUR = dhlWeightBaseForKg(kg);
            let carrierName = "DHL Paket";
            let path = "dhl_parcel_weight";

            if (!isFinite(baseEUR)) {
                // Fallback Spedition (ohne Zone)
                const over = Math.max(0, kg - DHL_MAX_KG);
                const steps = Math.ceil(over / FREIGHT_STEP_KG);
                const freightEUR = (FREIGHT_BASE[country] ?? 129.9) + steps * FREIGHT_STEP_SURCHARGE;
                const cost_cents = Math.round(freightEUR * 100);
                shippingPayload = {
                    method: effectiveMethod,
                    country,
                    zip,
                    path: "spedition_fallback",
                    carrier: "Spedition",
                    cost_cents,
                    zone_cents: 0,
                    breakdown: {
                        total_weight_kg: kg,
                        steps,
                        note: "parcel overweight fallback",
                    },
                };
            } else {
                const expressEUR = effectiveMethod === "express" ? (DHL_EXPRESS_SURCHARGE[country] ?? 0) : 0;
                const cost_cents = Math.round((baseEUR + expressEUR) * 100);
                shippingPayload = {
                    method: effectiveMethod,
                    country,
                    zip,
                    path,
                    carrier: carrierName,
                    cost_cents,
                    zone_cents: 0,
                    breakdown: {
                        total_weight_kg: kg,
                        base_cents: Math.round(baseEUR * 100),
                        express_cents: Math.round(expressEUR * 100),
                        express: effectiveMethod === "express",
                    },
                };
            }
        }

        
        // Adresse immer mitsenden (auch bei Abholung), damit sie in Mails/Bestellung immer vorhanden ist
        const addressText = `${street}\n${zip} ${city}\n${country}`;

        if (shippingPayload && method !== "pickup") {
            shippingPayload.street = street;
            shippingPayload.city = city;
        }
        try {
            const res = await fetch(`${API_BASE}/api/orders.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "omit",
                body: JSON.stringify({
                    email,
                    name,
                    phone,
                    address: addressText,
                    payment_method: paymentMethod,
                    items: items.map((it) => ({
                        slug: it.slug,
                        qty: it.qty,
                        variant_id: it.variant_id ?? null,
                        variant_label: it.variant_label ?? null,
                        price_cents: it.price_cents,
                    })),
                    shipping: shippingPayload,
                    totals: {
                        items_total_cents: itemsTotalCents,
                        shipping_cents: shippingPayload?.cost_cents ?? shippingTotalCents,
                        grand_total_cents: grandTotalCents,
                    },
                }),
            });
            if (res.ok) return await res.json();
            throw new Error(`HTTP ${res.status}`);
        } catch (err) {
            // KEIN Simulations-Fallback: Wenn die Bestellung nicht gespeichert werden
            // konnte, muss der Kunde einen Fehler sehen – sonst denkt er, er hätte
            // bestellt, obwohl nichts in der Datenbank steht.
            console.error("Checkout: Bestellung konnte nicht angelegt werden", err);
            throw new Error("Bestellung konnte nicht angelegt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.");
        }
    }

    async function createPaypalOrder(order_id) {
        const res = await fetch(`${API_BASE}/api/paypal.php?method=create`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "omit",
            // Zusätzlich erwartete Summen mitsenden.
            // Wenn dein paypal.php bisher nur order_id nutzt, kann es optional diese Werte übernehmen,
            // statt aus der DB neu zu rechnen.
            body: JSON.stringify({
                order_id,
                expected: {
                    items_total_cents: itemsTotalCents,
                    shipping_cents: shippingTotalCents,
                    grand_total_cents: grandTotalCents,
                },
            }),
        });
        if (!res.ok) throw new Error("PayPal create fehlgeschlagen");
        const data = await res.json();
        if (!data?.id) throw new Error("Ungültige PayPal-Antwort");
        return data.id;
    }

    async function capturePaypalOrder(paypal_order_id) {
        const res = await fetch(`${API_BASE}/api/paypal.php?method=capture`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "omit",
            body: JSON.stringify({ paypal_order_id }),
        });
        if (!res.ok) throw new Error("PayPal capture fehlgeschlagen");
        return res.json();
    }

    function renderPaypalButtons(currentOrder) {
        if (!window.paypal || !ppRef.current) {
            setErr("PayPal SDK nicht geladen. Bitte Client-ID in index.html prüfen.");
            return;
        }
        ppRef.current.innerHTML = "";
        window.paypal.Buttons({
            style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal" },
            createOrder: async () => {
                const id = await createPaypalOrder(currentOrder.order_id);
                return id;
            },
            onApprove: async (data) => {
                try {
                    await capturePaypalOrder(data.orderID);
                    setDone({
                        order_number: currentOrder.order_number,
                        total_cents: currentOrder.total_cents,
                    });
                    clearCart?.();
                } catch (e) {
                    setErr("Zahlung konnte nicht erfasst werden.");
                }
            },
            onError: (err) => {
                console.error(err);
                setErr("PayPal-Fehler. Bitte später erneut versuchen.");
            },
        }).render(ppRef.current);
    }

    /** ================= Submit ================= */
    // --- AUTOMATIC CITY LOOKUP ---
    useEffect(() => {
        if (country === "DE" && zip.length === 5) {
            setLoading(true); // Benutze existierenden Loading-State für Indikator
            fetch(`https://api.zippopotam.us/de/${zip}`)
                .then(r => r.ok && r.json())
                .then(data => {
                    if (data && data.places && data.places.length > 0) {
                        const cityName = data.places[0]['place name'];
                        if (cityName) setCity(cityName);
                    }
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        }
    }, [zip, country]);

    // --- Begonnenen Kaufvorgang sichern (für die Erinnerungs-Mail) ---
    // Sobald eine gültige E-Mail eingegeben wurde, wird der Warenkorb
    // serverseitig gespeichert. Bricht der Kunde ab, erinnert ihn der
    // Cronjob per Mail mit einem Link zum Fortsetzen.
    useEffect(() => {
        if (!email.includes("@") || !email.includes(".")) return;
        if (!items.length) return;

        const timer = setTimeout(() => {
            fetch(`${API_BASE}/api/save_cart.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "omit",
                body: JSON.stringify({
                    email,
                    name,
                    items: items.map((it) => ({
                        slug: it.slug,
                        title: it.title,
                        qty: it.qty,
                        price_cents: it.price_cents,
                        variant_id: it.variant_id ?? null,
                        variant_label: it.variant_label ?? null,
                    })),
                }),
            }).catch(() => {
                // Rein optional – der Checkout darf davon nie beeinträchtigt werden.
            });
        }, 1500); // kurz warten, damit nicht bei jedem Tastendruck gesendet wird

        return () => clearTimeout(timer);
    }, [email, name, items]);

    // Rechnungsbestellung bestätigen
    async function confirmInvoiceOrder(order_id) {
        const res = await fetch(`${API_BASE}/api/invoice.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "omit",
            body: JSON.stringify({ order_id }),
        });
        if (!res.ok) throw new Error("Rechnungsbestätigung fehlgeschlagen");
        return res.json();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr("");

        if (!items.length) return setErr("Dein Warenkorb ist leer.");
        if (!name.trim()) return setErr("Bitte deinen Namen eingeben.");
        if (!email.includes("@")) return setErr("Bitte eine gültige E-Mail eingeben.");
        if (country === "DE" && !isValidDEZip(zip)) {
            return setErr("Bitte eine gültige 5-stellige PLZ eingeben.");
        }
        if (!street.trim()) return setErr("Bitte Straße + Hausnummer eingeben.");
        if (!city.trim()) return setErr("Bitte Ort eingeben.");

        try {
            setSubmitting(true);
            const created = await createOrderInDB();
            setOrder(created);

            if (paymentMethod === "invoice") {
                // Rechnungsbestellung: Direkt bestätigen und E-Mail senden
                const confirmed = await confirmInvoiceOrder(created.order_id);
                setDone({
                    order_number: confirmed.order_number || created.order_number,
                    total_cents: confirmed.total_cents || created.total_cents,
                    payment_method: "invoice",
                    due_date: confirmed.due_date,
                });
                clearCart?.();
            } else {
                // PayPal: Buttons rendern
                renderPaypalButtons(created);
            }
        } catch (e) {
            setErr("Bestellung konnte nicht angelegt werden.");
        } finally {
            setSubmitting(false);
        }
    };

    /** ================= Done ================= */
    if (done) {
        const isInvoice = done.payment_method === "invoice";
        return (
            <section className="mx-auto max-w-3xl px-4 py-12">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-100 dark:bg-brand-900/30 mb-6">
                        <svg className="w-10 h-10 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
                        {isInvoice ? "Bestellung eingegangen!" : "Danke für deine Zahlung!"}
                    </h1>
                    <p className="mt-3 text-lg">Bestellnummer: <strong className="text-brand-600">{done.order_number}</strong></p>
                    <p className="text-lg">Summe: <strong>{(done.total_cents / 100).toFixed(2)} €</strong></p>
                    
                    {isInvoice && (
                        <div className="mt-6 p-6 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-800 text-left max-w-md mx-auto">
                            <h3 className="font-bold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                                <span>📋</span> Kauf auf Rechnung
                            </h3>
                            <p className="mt-2 text-amber-700 dark:text-amber-300 text-sm leading-relaxed">
                                Die Rechnung wird Ihrer Sendung beigelegt. Bitte überweisen Sie den Gesamtbetrag innerhalb von <strong>14 Tagen</strong> nach Erhalt der Ware.
                            </p>
                            {done.due_date && (
                                <p className="mt-2 text-amber-800 dark:text-amber-200 text-sm font-medium">
                                    Zahlungsziel: {done.due_date}
                                </p>
                            )}
                        </div>
                    )}
                    
                    <p className="mt-6 text-slate-600 dark:text-slate-300">
                        Du erhältst in Kürze eine Bestätigung per E-Mail.
                    </p>
                    
                    <a 
                        href="/" 
                        className="mt-8 inline-block px-6 py-3 bg-brand text-white font-semibold rounded-lg hover:bg-brandDark transition-colors"
                    >
                        Zurück zur Startseite
                    </a>
                </div>
            </section>
        );
    }

    // Anzeige
    const zoneInfoDE = country === "DE" ? findDEZone(zip) : null;
    const zoneEUR =
        hasSpeditionItem && country === "DE" && isValidDEZip(zip)
            ? (zoneInfoDE?.zone_cents || 0) / 100
            : hasSpeditionItem && country !== "DE" && typeof COUNTRY_ZONE_FLAT[country] === "number"
                ? COUNTRY_ZONE_FLAT[country] / 100
                : 0;

    const canExpress = !hasSpeditionItem && !hasSperrgutItem && totalWeightKg > 0 && totalWeightKg <= DHL_MAX_KG;

    /* ---- Shared: Order Summary content ---- */
    const orderSummaryContent = (
        <>
            {loading ? (
                <div className="flex items-center justify-center gap-3 text-slate-400 py-8">
                    <div className="h-5 w-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Lade Artikel…</span>
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-slate-400 italic text-sm">Dein Warenkorb ist leer.</p>
                    <Link to="/shop" className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" /></svg>
                        Zum Shop
                    </Link>
                </div>
            ) : (
                <>
                    <div className="space-y-3">
                        {items.map((it) => (
                            <div key={`${it.slug}-${it.variant_id ?? "base"}`} className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 leading-snug">
                                        <span className="text-brand-600 font-bold tabular-nums mr-1.5">{it.qty}×</span>
                                        {it.title}
                                    </div>
                                    {it.variant_label && (
                                        <span className="mt-0.5 inline-block text-[10px] font-bold uppercase tracking-wider text-brand-500/70 bg-brand-50 dark:bg-brand-900/30 px-1.5 py-px rounded">
                                            {it.variant_label}
                                        </span>
                                    )}
                                </div>
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 tabular-nums whitespace-nowrap">
                                    {((it.price_cents * it.qty) / 100).toFixed(2)}&nbsp;€
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-500">
                            <span>Zwischensumme</span>
                            <span className="tabular-nums">{itemsTotalEUR.toFixed(2)}&nbsp;€</span>
                        </div>

                        {/* Versand */}
                        <div className="py-3 px-4 bg-brand-50/60 dark:bg-brand-950/20 rounded-xl border border-brand-100/40 dark:border-brand-900/30 space-y-1.5">
                            {method === "pickup" ? (
                                <div className="flex justify-between text-brand-700 dark:text-brand-300 font-medium text-sm">
                                    <span>Abholung vor Ort</span>
                                    <span className="tabular-nums">0,00&nbsp;€</span>
                                </div>
                            ) : carrier === "Spedition" ? (
                                <>
                                    <div className="flex justify-between text-brand-700 dark:text-brand-200 font-bold text-sm">
                                        <span>Spedition ({totalWeightKg.toFixed(1)}&nbsp;kg)</span>
                                        <span className="tabular-nums">{(shippingEUR || 0).toFixed(2)}&nbsp;€</span>
                                    </div>
                                    <div className="space-y-0.5 pt-1.5 border-t border-brand-200/30">
                                        <div className="flex justify-between text-[10px] uppercase tracking-wider text-brand-600/60 font-bold">
                                            <span>Grundfracht {country}</span>
                                            <span>{(totalWeightKg <= 84.9 ? 80.0 : (FREIGHT_BASE[country] || 0)).toFixed(2)}&nbsp;€</span>
                                        </div>
                                        {zoneEUR > 0 && (
                                            <div className="flex justify-between text-[10px] uppercase tracking-wider text-brand-600/60 font-bold">
                                                <span>PLZ-Zuschlag {zoneLabel && `(${zoneLabel})`}</span>
                                                <span>{zoneEUR.toFixed(2)}&nbsp;€</span>
                                            </div>
                                        )}
                                        {totalWeightKg >= 200 && (
                                            <div className="flex justify-between text-[10px] uppercase tracking-wider text-brand-600/60 font-bold">
                                                <span>Gewichtszuschlag</span>
                                                <span>{((shippingEUR || 0) - (totalWeightKg <= 84.9 ? 80.0 : (FREIGHT_BASE[country] || 0)) - zoneEUR).toFixed(2)}&nbsp;€</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex justify-between text-brand-700 dark:text-brand-200 font-bold text-sm">
                                    <span>
                                        {carrier}{totalWeightKg > 0 ? ` (${totalWeightKg.toFixed(1)} kg)` : ""}
                                        {method === "express" && carrier === "DHL Paket" ? " Express" : ""}
                                    </span>
                                    <span className="tabular-nums">{(shippingEUR || 0).toFixed(2)}&nbsp;€</span>
                                </div>
                            )}
                        </div>

                        {/* Gesamt */}
                        <div className="flex justify-between items-baseline pt-4 border-t-2 border-slate-200 dark:border-slate-700">
                            <span className="text-base font-heading font-black text-slate-900 dark:text-white uppercase tracking-tight">Gesamt</span>
                            <span className="text-2xl font-heading font-black text-brand-600 tabular-nums">
                                {grandTotalEUR.toFixed(2)}&nbsp;€
                            </span>
                        </div>
                        <p className="text-[10px] text-slate-400 italic text-center pt-1">
                            Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.
                        </p>
                    </div>
                </>
            )}
        </>
    );

    /* ---- Helper: styled label + input ---- */
    const inputCls =
        "w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-colors";
    const labelCls = "block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5";
    const sectionCard =
        "rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6";

    return (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12">
            {/* Header */}
            <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white">Zur Kasse</h1>
                    <p className="mt-1 text-sm text-slate-500">Sichere Bestellung – wenige Schritte</p>
                </div>
                <Link
                    to="/warenkorb"
                    className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium whitespace-nowrap mt-1 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                    Warenkorb
                </Link>
            </div>

            {/* ====== MOBILE: collapsible order summary ====== */}
            <div className="lg:hidden mb-6">
                <button
                    type="button"
                    onClick={() => setSummaryOpen(!summaryOpen)}
                    className="w-full flex items-center justify-between rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 shadow-sm"
                >
                    <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                        <span className="font-semibold text-slate-900 dark:text-white text-sm">
                            Bestellung ({items.length} {items.length === 1 ? "Artikel" : "Artikel"})
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-brand-600 tabular-nums">{grandTotalEUR.toFixed(2)}&nbsp;€</span>
                        <svg className={`w-4 h-4 text-slate-400 transition-transform ${summaryOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </button>
                {summaryOpen && (
                    <div className="mt-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
                        {items.length > 0 && (
                            <div className="flex justify-end mb-3">
                                <Link to="/warenkorb" className="text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors">
                                    Bearbeiten
                                </Link>
                            </div>
                        )}
                        {orderSummaryContent}
                    </div>
                )}
            </div>

            {/* ====== 2-COLUMN GRID ====== */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

                {/* ---- LEFT: Form ---- */}
                <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-6">
                    {err && (
                        <div className="flex items-center gap-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {err}
                        </div>
                    )}

                    {/* --- 1. Kontaktdaten --- */}
                    <div className={sectionCard}>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-black">1</div>
                            <h2 className="text-base font-heading font-bold text-slate-900 dark:text-white">Kontaktdaten</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className={labelCls}>Name *</label>
                                <input className={inputCls} placeholder="Vor- und Nachname" value={name} onChange={(e) => setName(e.target.value)} required />
                            </div>
                            <div>
                                <label className={labelCls}>E-Mail *</label>
                                <input className={inputCls} type="email" placeholder="ihre@email.de" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                <p className="mt-1.5 text-[11px] leading-snug text-slate-400 dark:text-slate-500">
                                    Falls Sie die Bestellung nicht abschließen, erinnere ich Sie einmalig
                                    per E-Mail daran. Sie können dem jederzeit widersprechen – ein
                                    Abmelde-Link ist in jeder Erinnerung enthalten.
                                </p>
                            </div>
                            <div>
                                <label className={labelCls}>Telefon *</label>
                                <input className={inputCls} type="tel" placeholder="+49 123 456789" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+ ]/g, ""))} required />
                            </div>
                        </div>
                    </div>

                    {/* --- 2. Lieferadresse + Versand --- */}
                    <div className={sectionCard}>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-black">2</div>
                            <h2 className="text-base font-heading font-bold text-slate-900 dark:text-white">Lieferung</h2>
                        </div>

                        {/* Versandart-Karten */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
                            {[
                                { value: "standard", icon: "📦", label: "Standard", sub: carrier !== "Pickup" ? `${carrier}` : "" },
                                { value: "express", icon: "⚡", label: "Express", sub: "DHL", disabled: !canExpress },
                                { value: "pickup", icon: "🏠", label: "Abholung", sub: "Vor Ort" },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    disabled={opt.disabled}
                                    onClick={() => setMethod(opt.value)}
                                    className={`relative flex flex-col items-center gap-1 p-4 rounded-xl border-2 transition-all text-center ${
                                        method === opt.value
                                            ? "border-brand-500 bg-brand-50/50 dark:bg-brand-900/20 shadow-sm"
                                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                                    } ${opt.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    <span className="text-xl">{opt.icon}</span>
                                    <span className="text-sm font-bold text-slate-800 dark:text-white">{opt.label}</span>
                                    {opt.sub && <span className="text-[10px] text-slate-400 uppercase tracking-wider">{opt.sub}</span>}
                                    {method === opt.value && (
                                        <div className="absolute top-2 right-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-500" />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Hinweis bei Abholung */}
                        {method === "pickup" && (
                            <div className="flex items-center gap-3 py-3 px-4 rounded-xl bg-brand-50/60 dark:bg-brand-900/20 border border-brand-100/50 dark:border-brand-800/30 text-sm text-brand-700 dark:text-brand-300 mb-4">
                                <span className="text-lg">📍</span>
                                Abholung vor Ort – bitte trotzdem Rechnungsadresse angeben.
                            </div>
                        )}

                        {/* Adressfelder (immer sichtbar – Liefer- oder Rechnungsadresse) */}
                        {method === "pickup" && (
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Rechnungsadresse</p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>Land</label>
                                <select className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)}>
                                    <option value="DE">Deutschland</option>
                                    <option value="AT">Österreich</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>PLZ *</label>
                                <input
                                    className={inputCls}
                                    placeholder={country === "DE" ? "z.B. 93326" : "PLZ"}
                                    inputMode="numeric"
                                    value={zip}
                                    onChange={(e) => setZip(sanitizeZipTyping(e.target.value))}
                                    onBlur={(e) => setZip(sanitizeZipTyping(e.target.value))}
                                    required
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Ort *</label>
                                <input className={inputCls} placeholder="Ort" value={city} onChange={(e) => setCity(e.target.value)} required />
                            </div>
                            <div>
                                <label className={labelCls}>Straße + Nr. *</label>
                                <input className={inputCls} placeholder="Musterstr. 1" value={street} onChange={(e) => setStreet(e.target.value)} required />
                            </div>
                        </div>

                        {/* Speditions-PLZ-Hinweis */}
                        {method !== "pickup" && hasSpeditionItem && (
                            <p className="mt-3 text-xs text-slate-500">
                                {country === "DE"
                                    ? isValidDEZip(zip)
                                        ? zoneLabel ? `📍 PLZ-Zone: ${zoneLabel}` : "✓ PLZ erkannt"
                                        : "Bitte 5-stellige PLZ eingeben (Zonenpreis für Spedition)"
                                    : "Pauschalaufschlag für Spedition außerhalb Deutschlands"}
                            </p>
                        )}
                    </div>

                    {/* --- 3. Zahlungsart --- */}
                    <div className={sectionCard}>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-black">3</div>
                            <h2 className="text-base font-heading font-bold text-slate-900 dark:text-white">Zahlungsart</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {[
                                { value: "paypal", icon: "💳", label: "PayPal / Kreditkarte", sub: "Sofortige Zahlung" },
                                { value: "invoice", icon: "📋", label: "Kauf auf Rechnung", sub: "Zahlung in 14 Tagen" },
                            ].map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`relative flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        paymentMethod === opt.value
                                            ? "border-brand-500 bg-brand-50/50 dark:bg-brand-900/20"
                                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value={opt.value}
                                        checked={paymentMethod === opt.value}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="sr-only"
                                    />
                                    <span className="text-2xl">{opt.icon}</span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold text-slate-800 dark:text-white">{opt.label}</div>
                                        <div className="text-xs text-slate-400">{opt.sub}</div>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        paymentMethod === opt.value
                                            ? "border-brand-500 bg-brand-500"
                                            : "border-slate-300 dark:border-slate-600"
                                    }`}>
                                        {paymentMethod === opt.value && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>
                        {paymentMethod === "invoice" && (
                            <div className="mt-4 flex items-start gap-3 py-3 px-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40 text-sm text-amber-700 dark:text-amber-300">
                                <span className="text-base flex-shrink-0">💡</span>
                                <span>Die Rechnung liegt der Sendung bei. Bitte überweisen Sie den Betrag nach Erhalt der Ware.</span>
                            </div>
                        )}
                    </div>

                    {/* --- Submit --- */}
                    <button
                        type="submit"
                        disabled={submitting || loading || items.length === 0}
                        className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white font-bold text-base py-4 px-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-brand-600/20"
                    >
                        {submitting ? (
                            <>
                                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Bitte warten…
                            </>
                        ) : paymentMethod === "invoice" ? (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                Jetzt auf Rechnung bestellen
                            </>
                        ) : order ? (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                PayPal erneut laden
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                Weiter zu PayPal
                            </>
                        )}
                    </button>

                    {/* PayPal Buttons */}
                    {paymentMethod === "paypal" && (
                        <div className="mt-2">
                            <div ref={ppRef} />
                            {order && !window.paypal && (
                                <p className="mt-2 text-sm text-red-600">
                                    PayPal SDK nicht geladen – prüfe die Client-ID in <code>public/index.html</code>.
                                </p>
                            )}
                        </div>
                    )}
                </form>

                {/* ---- RIGHT: Sticky Order Summary (Desktop only) ---- */}
                <aside className="hidden lg:block lg:col-span-2">
                    <div className="sticky top-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg shadow-slate-200/40 dark:shadow-none overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                                <span className="font-heading font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider text-xs">Deine Bestellung</span>
                            </div>
                            {items.length > 0 && (
                                <Link to="/warenkorb" className="text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors">
                                    Bearbeiten
                                </Link>
                            )}
                        </div>
                        <div className="p-6">
                            {orderSummaryContent}
                        </div>
                    </div>
                </aside>
            </div>
        </section>
    );
}
